import React, { useMemo, useState } from "react";
import { useTheme } from "../../Utils/useTheme";

type Props = {
  children?: any;
  onClick?: (e: any) => void;
  variant?: "default" | "primary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  loading?: boolean;
  full?: boolean;
  icon?: any;
  rightIcon?: any;
  imageSrc?: string;
  imageAlt?: string;
  imageSize?: number;
  type?: "button" | "submit" | "reset";
  title?: string;
  style?: any;
};

const SIZE: any = {
  sm: { h: 32, px: 12, py: 6, fs: 13, r: 10, g: 8 },
  md: { h: 38, px: 14, py: 8, fs: 14, r: 12, g: 10 },
  lg: { h: 44, px: 16, py: 10, fs: 15, r: 14, g: 10 },
};

const Spinner = ({ size = 14, color }: any) => {
  const [id] = useState(() => `sp_${Math.random().toString(16).slice(2)}`);
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        border: `2px solid ${color}`,
        borderTopColor: "transparent",
        display: "inline-block",
        animation: `${id} 0.8s linear infinite`,
      }}
    >
      <style>{`@keyframes ${id}{to{transform:rotate(360deg)}}`}</style>
    </span>
  );
};

export const Button = ({
  children,
  onClick,
  variant = "default",
  size = "md",
  disabled = false,
  loading = false,
  full = false,
  icon,
  rightIcon,
  imageSrc,
  imageAlt = "button",
  imageSize,
  type = "button",
  title,
  style,
}: Props) => {
  const { isDark, text } = useTheme();

  const s = SIZE[size] || SIZE.md;
  const imgS = imageSize ?? (size === "sm" ? 16 : size === "lg" ? 20 : 18);

  const colors = useMemo(() => {
    if (variant === "primary")
      return {
        bg: isDark ? "#4F46E5" : "#6366F1",
        fg: "#ffffff",
        bd: "transparent",
        sh: "rgba(99,102,241,0.28)",
      };

    if (variant === "danger")
      return {
        bg: isDark ? "#B91C1C" : "#DC2626",
        fg: "#ffffff",
        bd: "transparent",
        sh: "rgba(220,38,38,0.28)",
      };

    if (variant === "ghost")
      return {
        bg: "transparent",
        fg: text,
        bd: "transparent",
        sh: "transparent",
      };

    return {
      bg: isDark ? "rgba(255,255,255,0.06)" : "#FFFFFF",
      fg: text,
      bd: isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.12)",
      sh: isDark ? "rgba(0,0,0,0.22)" : "rgba(15,23,42,0.14)",
    };
  }, [variant, isDark, text]);

  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);
  const isDisabled = disabled || loading;

  const IconLeft = icon;
  const IconRight = rightIcon;

  return (
    <button
      type={type}
      title={title}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setPress(false);
      }}
      onMouseDown={() => setPress(true)}
      onMouseUp={() => setPress(false)}
      style={{
        height: s.h,
        padding: `${s.py}px ${s.px}px`,
        borderRadius: s.r,
        border: `1px solid ${colors.bd}`,
        background: colors.bg,
        color: colors.fg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.g,
        width: full ? "100%" : undefined,
        cursor: isDisabled ? "not-allowed" : "pointer",
        userSelect: "none",
        fontWeight: 600,
        fontSize: s.fs,
        outline: "none",
        position: "relative",
        overflow: "hidden",
        boxShadow:
          variant === "ghost"
            ? "none"
            : press
            ? `0 1px 3px ${colors.sh}`
            : hover
            ? `0 3px 8px ${colors.sh}`
            : `0 2px 6px ${colors.sh}`,
        transform: press
          ? "translateY(0.5px)"
          : hover
          ? "translateY(-0.5px)"
          : "translateY(0)",
        transition: "all 160ms cubic-bezier(.2,.8,.2,1)",
        opacity: isDisabled ? 0.6 : 1,
        ...style,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: s.g }}>
        {loading ? (
          <Spinner size={size === "sm" ? 12 : 14} color={colors.fg} />
        ) : (
          <>
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={imageAlt}
                style={{
                  width: imgS,
                  height: imgS,
                  borderRadius: 6,
                  objectFit: "cover",
                }}
                draggable={false}
              />
            ) : IconLeft ? (
              <IconLeft size={size === "sm" ? 16 : size === "lg" ? 20 : 18} />
            ) : null}
          </>
        )}

        <span style={{ whiteSpace: "nowrap" }}>{children}</span>

        {!loading && IconRight ? (
          <IconRight size={size === "sm" ? 16 : size === "lg" ? 20 : 18} />
        ) : null}
      </span>
    </button>
  );
};
